import { useCallback, useMemo } from "react";

import { stringToBoolean } from "@/lib/chillnn/string-to-boolean";
import { CurrencyType, KidsCosleepingPolicyType } from "@/lib/gql/__generated__/graphql";

import { KIDS_COSLEEPING_POLICY_DISCOUNT_TYPE_VARIABLES } from "@/components/features/items/edit/_shared/constants/kids-policy";
import type { AccommodationHotelPolicyFormModel } from "@/components/features/items/edit/hotel-policy/default/model/type";
import { useAccommodationHotelPolicyFormModelContext } from "@/components/features/items/edit/hotel-policy/default/ui/context";

import { getOptionNumArray } from "@/components/ui/form/_utils";

const kidsCosleepingPolicyInitialValue = {
	isActive: {
		isEdited: true,
		value: true,
	},
	kidsDiscountType: {
		isEdited: true,
		value: KidsCosleepingPolicyType.Ratio,
	},
	ratio: {
		isEdited: true,
		value: 0,
	},
	kidsMaxAge: {
		isEdited: true,
		value: 0,
	},
	static: {
		amount: {
			isEdited: true,
			value: 1000,
		},
		currencyType: {
			isEdited: true,
			value: CurrencyType.Jpy,
		},
	},
} satisfies AccommodationHotelPolicyFormModel["ViewModel"]["kidsCosleepingPolicy"];

export const useKidsCosleepingPolicyOfHotelPolicy = () => {
	const { viewModel, onSetViewModel } = useAccommodationHotelPolicyFormModelContext();
	const { kidsCosleepingPolicy } = viewModel;

	const activate = useCallback(() => {
		onSetViewModel([{ key: "kidsCosleepingPolicy", value: kidsCosleepingPolicyInitialValue }]);
	}, [onSetViewModel]);

	const deactivate = useCallback(() => {
		const value = {
			...kidsCosleepingPolicyInitialValue,
			isActive: {
				isEdited: true,
				value: false,
			},
		};
		onSetViewModel([{ key: "kidsCosleepingPolicy", value }]);
	}, [onSetViewModel]);

	const discountTypeOptions = KIDS_COSLEEPING_POLICY_DISCOUNT_TYPE_VARIABLES.map((item) => {
		return {
			id: item.key,
			label: item.key,
			value: item.value,
		};
	});

	const onUpdateIsActive = useCallback(
		(value: string) => {
			if (value === "true" || value === "false") {
				const booleanValue = stringToBoolean(value);

				if (booleanValue) {
					activate();
				} else {
					deactivate();
				}
			}
		},
		[activate, deactivate],
	);

	const handleDiscountTypeSelect = useCallback(
		(value: string) => {
			if (kidsCosleepingPolicy && kidsCosleepingPolicy.isActive.value) {
				const newKidsCosleepingPolicy = {
					...kidsCosleepingPolicy,
					kidsDiscountType: {
						isEdited: true,
						value: value as KidsCosleepingPolicyType,
					},
				} satisfies AccommodationHotelPolicyFormModel["ViewModel"]["kidsCosleepingPolicy"];

				onSetViewModel([{ key: "kidsCosleepingPolicy", value: newKidsCosleepingPolicy }]);
			}
		},
		[kidsCosleepingPolicy, onSetViewModel],
	);

	const maxAgeOptions = getOptionNumArray(0, 1, 13);
	const handleMaxAgeChange = useCallback(
		(value: string) => {
			const maxAge = Number(value);

			if (typeof maxAge !== "number" || maxAge < 0) return;

			if (kidsCosleepingPolicy && kidsCosleepingPolicy.isActive.value) {
				const newKidsCosleepingPolicy = {
					...kidsCosleepingPolicy,
					kidsMaxAge: {
						isEdited: true,
						value: maxAge,
					},
				} satisfies AccommodationHotelPolicyFormModel["ViewModel"]["kidsCosleepingPolicy"];

				onSetViewModel([{ key: "kidsCosleepingPolicy", value: newKidsCosleepingPolicy }]);
			}
		},
		[kidsCosleepingPolicy, onSetViewModel],
	);

	const ratioOptions = getOptionNumArray(0, 5, 21, { suffix: "％" });
	const handleRatioSelect = useCallback(
		(value: string) => {
			const ratio = Number(value);

			if (typeof ratio !== "number" || ratio < 0 || ratio > 100) return;

			if (kidsCosleepingPolicy && kidsCosleepingPolicy.isActive.value) {
				const newKidsCosleepingPolicy = {
					...kidsCosleepingPolicy,
					ratio: {
						isEdited: true,
						value: ratio,
					},
				} satisfies AccommodationHotelPolicyFormModel["ViewModel"]["kidsCosleepingPolicy"];

				onSetViewModel([{ key: "kidsCosleepingPolicy", value: newKidsCosleepingPolicy }]);
			}
		},
		[kidsCosleepingPolicy, onSetViewModel],
	);

	const handleStaticAmountChange = useCallback(
		(amount: number | undefined) => {
			if (kidsCosleepingPolicy && kidsCosleepingPolicy.isActive.value) {
				const newKidsCosleepingPolicy = {
					...kidsCosleepingPolicy,
					static: {
						...kidsCosleepingPolicy.static,
						amount: {
							isEdited: true,
							value: amount!,
						},
						currencyType: {
							isEdited: true,
							value: CurrencyType.Jpy,
						},
					},
				} satisfies AccommodationHotelPolicyFormModel["ViewModel"]["kidsCosleepingPolicy"];

				onSetViewModel([{ key: "kidsCosleepingPolicy", value: newKidsCosleepingPolicy }]);
			}
		},
		[kidsCosleepingPolicy, onSetViewModel],
	);

	const isEdited = kidsCosleepingPolicy?.isActive?.value ?? false;
	const hasError = Boolean(
		kidsCosleepingPolicy?.isActive?.error ??
			kidsCosleepingPolicy?.kidsDiscountType?.error ??
			kidsCosleepingPolicy?.kidsMaxAge?.error ??
			kidsCosleepingPolicy?.ratio?.error ??
			kidsCosleepingPolicy?.static?.amount.error,
	);

	const isActiveString = useMemo(
		() => String(kidsCosleepingPolicy?.isActive?.value ?? false),
		[kidsCosleepingPolicy],
	);
	const isActiveBoolean = useMemo(() => stringToBoolean(isActiveString), [isActiveString]);

	return {
		kidsCosleepingPolicy,
		isEdited,
		hasError,
		isActiveString,
		isActiveBoolean,
		onUpdateIsActive,
		discountTypeOptions,
		handleDiscountTypeSelect,
		maxAgeOptions,
		handleMaxAgeChange,
		ratioOptions,
		handleRatioSelect,
		handleStaticAmountChange,
	};
};
